/**
 * Android POS built-in printer adapter.
 *
 * Contract implemented (see printerService.js):
 *   - mode              : 'android_builtin'
 *   - isAvailable()     : Promise<boolean>
 *   - getStatus()       : Promise<{available, deviceName, error?}>
 *   - printReceipt(order, opts?) : Promise<{success, error?}>
 *   - testPrint(opts?)  : Promise<{success, error?}>
 *
 * ============================================================================
 * How this works
 * ============================================================================
 * The web app is expected to run inside a WebView shipped by the customer as
 * a lightweight native Android wrapper. That wrapper uses
 * `WebView.addJavascriptInterface(printerBridge, "AndroidPrinter")` to inject
 * a JavaScript interface named `window.AndroidPrinter`. This adapter is the
 * ONLY web-side code that knows the wire format for that bridge, so future
 * hardware SDKs (SUNMI, iMin, PAX, etc.) can be added by:
 *
 *   1. Building a native module against that vendor's Java/Kotlin printer SDK.
 *   2. Exposing exactly the four methods below via addJavascriptInterface
 *      (name = "AndroidPrinter"). All strings — no fancy types.
 *   3. Deploying the wrapped APK on the POS device.
 *
 * NO WEB-SIDE CODE CHANGES ARE REQUIRED to add a new POS vendor.
 *
 * ============================================================================
 * Expected JS bridge shape (window.AndroidPrinter)
 * ============================================================================
 *   window.AndroidPrinter = {
 *     // 1) Synchronous probe. Return "true" or "false" as a plain string.
 *     isAvailable(): string,
 *
 *     // 2) Return a friendly device label. E.g. "SUNMI T2", "iMin M2 Max".
 *     getDeviceName(): string,
 *
 *     // 3) Print ESC/POS bytes encoded as a hex string (two chars per byte,
 *     //    upper or lower case, no separators). Returns "OK" on success or
 *     //    a short error string on failure. May be synchronous — the native
 *     //    side is free to block up to ~5s while it drives the thermal head.
 *     printEscpos(escposHex: string): string,
 *
 *     // 4) Optional. Cash-drawer kick / feed / etc. Not used by this
 *     //    adapter yet but reserved.
 *     runCommand?(name: string, arg?: string): string,
 *   }
 *
 * If any of the required methods is missing, isAvailable() returns false and
 * this adapter never attempts a print — the caller (printerService) will
 * surface a clear "Built-in printer unavailable" status.
 * ============================================================================
 */

import { formatReceipt } from '../../utils/qzPrinter';

// ---------------------------------------------------------------------------
// Bridge probe helpers
// ---------------------------------------------------------------------------
const getBridge = () => {
  if (typeof window === 'undefined') return null;
  const b = window.AndroidPrinter;
  if (!b || typeof b !== 'object') return null;
  return b;
};

const bridgeHasMethod = (name) => {
  const b = getBridge();
  return !!(b && typeof b[name] === 'function');
};

const truthy = (v) => v === true || v === 'true' || v === 1 || v === '1' || v === 'OK' || v === 'ok';

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------
/**
 * Convert an ESC/POS string (produced by formatReceipt) to a hex string
 * suitable for the JS↔Java bridge. Uses CP858 codepage semantics — the
 * receipt formatter already emits raw bytes in that range for symbols like £.
 *
 * We map each JS 16-bit code unit to a single byte modulo 256. This mirrors
 * how QZ Tray's `format: 'command'` / `encoding: 'CP858'` interprets the
 * outgoing bytes, so identical receipts print byte-for-byte the same on
 * both adapters.
 */
const escposToHex = (payload) => {
  let out = '';
  for (let i = 0; i < payload.length; i += 1) {
    const b = payload.charCodeAt(i) & 0xff;
    out += b.toString(16).padStart(2, '0');
  }
  return out;
};

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------
export const androidPosPrinterAdapter = {
  mode: 'android_builtin',

  async isAvailable() {
    if (!bridgeHasMethod('isAvailable') || !bridgeHasMethod('printEscpos')) {
      return false;
    }
    try {
      return truthy(window.AndroidPrinter.isAvailable());
    } catch {
      return false;
    }
  },

  async getStatus() {
    if (!bridgeHasMethod('isAvailable') || !bridgeHasMethod('printEscpos')) {
      return {
        available: false,
        deviceName: null,
        error: 'No Android printer bridge detected. Install the POS wrapper app on this device.',
      };
    }
    let deviceName = 'Built-in Printer';
    try {
      if (bridgeHasMethod('getDeviceName')) {
        const n = window.AndroidPrinter.getDeviceName();
        if (n) deviceName = String(n);
      }
    } catch {
      /* keep default label */
    }
    const available = await this.isAvailable();
    return {
      available,
      deviceName,
      error: available ? null : 'Built-in printer reported not-ready',
    };
  },

  async printReceipt(order /* , opts = {} */) {
    if (!(await this.isAvailable())) {
      return { success: false, error: 'Built-in Android printer unavailable' };
    }
    try {
      const escposPayload = formatReceipt(order);
      const hex = escposToHex(escposPayload);
      const res = window.AndroidPrinter.printEscpos(hex);
      if (truthy(res)) return { success: true };
      return { success: false, error: String(res || 'Native printer returned no confirmation') };
    } catch (err) {
      return { success: false, error: String(err?.message || err).slice(0, 300) };
    }
  },

  async testPrint(/* opts = {} */) {
    // A minimal representative order so operators can validate the built-in
    // printer with one tap. Uses the same formatReceipt() renderer as the
    // network adapter so the layout matches byte-for-byte.
    const testOrder = {
      id: 'TESTABC123',
      daily_order_number: 99,
      customer_name: 'Mr Test Customer',
      customer_phone: '07123 456 789',
      order_type: 'collection',
      payment_method: 'cod',
      items: [
        { name: 'Test Item', quantity: 1, price: 4.99 },
      ],
      subtotal: 4.99,
      total_amount: 4.99,
      notes: 'Test print from Built-in POS Printer',
      is_preorder: false,
    };
    return this.printReceipt(testOrder);
  },
};

export default androidPosPrinterAdapter;
